import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Save, Plus, Globe, Upload, Trash2, Loader2, FileText, Sparkles, Maximize2, X, User } from 'lucide-react';
import { supabase } from '../utils/supabaseStorage';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { PageSettingsModal } from '../components/admin/PageSettingsModal';
import { FormSection as FormSectionBlock } from '../components/admin/FormSection';
import { FieldSettingsModal } from '../components/admin/FieldSettingsModal';
import type { FormField, FormSection } from '../types/formBuilder';
import type { AgentCategory, AgentKbDocument, AgentPricing, AgentTemplate, CentralFalTool } from '../types/agent';
import type { AgentTtsProvider } from '../utils/agentVoiceConfig';
import {
  buildVoiceConfigForSave,
  isValidElevenlabsVoiceIdInput,
  OPENAI_TTS_VOICE_OPTIONS,
  parseVoiceConfigFromRow,
  STT_LOCALE_OPTIONS,
} from '../utils/agentVoiceConfig';
import { createNewField, createNewSection, validateFormConfig } from '../utils/formBuilderHelpers';
import {
  defaultOnboardingSections,
  flattenOnboardingFields,
  getOnboardingAnswerKey,
  normalizeOnboardingSections,
} from '../utils/onboardingFormSchema';
import {
  agentReindexKb,
  agentGenerateCoverImage,
  agentGenerateAvatarImage,
  agentSynthesizeKbDraft,
  agentExpandAgentSystem,
} from '../utils/agentApi';
import { parseAdminStaffSession } from '../utils/adminSession';
import { AdminPageToolbar } from '../components/admin/AdminLayout';

const INPUT =
  'w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all';
const TEXTAREA = `${INPUT} resize-none min-h-[120px]`;

function loadImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });
}

function loadVideoDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    v.playsInline = true;
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve({ width: v.videoWidth, height: v.videoHeight });
    };
    v.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read video'));
    };
    v.src = url;
  });
}

/** Returns null if dimensions could not be read (still allow upload). */
async function landscapeHintFromFile(file: File): Promise<string | null> {
  try {
    if (file.type.startsWith('image/')) {
      const { width, height } = await loadImageDimensions(file);
      if (width > 0 && height > 0 && width < height) {
        return 'This image looks portrait. Landscape (wider than tall), e.g. 16:9, works best for the cover.';
      }
    } else if (file.type.startsWith('video/')) {
      const { width, height } = await loadVideoDimensions(file);
      if (width > 0 && height > 0 && width < height) {
        return 'This video looks portrait. Landscape (wider than tall), e.g. 16:9, works best for the cover.';
      }
    }
  } catch {
    return null;
  }
  return null;
}

async function uploadAgentCoverToStorage(
  file: File,
  templateId: string
): Promise<{ url: string; type: 'image' | 'video' }> {
  const isVideo = file.type.startsWith('video/');
  const isImage = file.type.startsWith('image/');
  if (!isVideo && !isImage) {
    throw new Error('Choose an image or video file');
  }
  const rawExt = file.name.includes('.') ? file.name.split('.').pop() : '';
  const ext =
    rawExt && /^[a-zA-Z0-9]+$/.test(rawExt) ? rawExt.toLowerCase() : isVideo ? 'mp4' : 'jpg';
  const safe = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const path = `agent-templates/${templateId}/cover-${safe}.${ext}`;
  const { error } = await supabase.storage.from('uploaded').upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw error;
  const { data } = supabase.storage.from('uploaded').getPublicUrl(path);
  return { url: data.publicUrl, type: isVideo ? 'video' : 'image' };
}

function buildPricingFromState(s: {
  freeEnabled: boolean;
  dayEnabled: boolean;
  dayCredits: number;
  monthEnabled: boolean;
  monthCredits: number;
  yearEnabled: boolean;
  yearCredits: number;
}): AgentPricing {
  const p: AgentPricing = {};
  if (s.freeEnabled) p.free = true;
  if (s.dayEnabled) p.day = Math.max(0, s.dayCredits);
  if (s.monthEnabled) p.month = Math.max(0, s.monthCredits);
  if (s.yearEnabled) p.year = Math.max(0, s.yearCredits);
  return p;
}

function hydratePricingState(pricing: AgentPricing | null | undefined) {
  const p = pricing || {};
  return {
    freeEnabled: p.free === true,
    dayEnabled: typeof p.day === 'number' && p.day >= 0,
    dayCredits: typeof p.day === 'number' ? p.day : 10,
    monthEnabled: typeof p.month === 'number' && p.month >= 0,
    monthCredits: typeof p.month === 'number' ? p.month : 100,
    yearEnabled: typeof p.year === 'number' && p.year >= 0,
    yearCredits: typeof p.year === 'number' ? p.year : 1000,
  };
}

function normalizeTemplateTags(tags: string[]): string[] {
  const out = new Set<string>();
  for (const t of tags) {
    const n = t.trim().toLowerCase();
    if (n) out.add(n);
  }
  return Array.from(out).sort();
}

type AgentFormTab =
  | 'public'
  | 'basic'
  | 'system'
  | 'onboarding'
  | 'pricing'
  | 'knowledge'
  | 'capabilities';

const AGENT_FORM_TABS: { id: AgentFormTab; label: string }[] = [
  { id: 'public', label: 'Public page' },
  { id: 'basic', label: 'Basic info' },
  { id: 'system', label: 'System & model' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'knowledge', label: 'Knowledge base' },
  { id: 'capabilities', label: 'Features & tools' },
];

export const AgentTemplateFormPage = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(templateId && templateId !== 'create');
  /** Saved agent UUID — set after first create so public page settings can open (same pattern as model + PageSettingsModal). */
  const savedAgentId = isEdit && templateId ? templateId : undefined;
  const canConfigurePublicPage = Boolean(savedAgentId);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [showPageSettings, setShowPageSettings] = useState(false);

  const [name, setName] = useState('');
  const [publicDescription, setPublicDescription] = useState('');
  const [coverMediaUrl, setCoverMediaUrl] = useState('');
  const [coverMediaType, setCoverMediaType] = useState<'image' | 'video'>('image');
  const [systemInstructions, setSystemInstructions] = useState('You are a helpful assistant.');
  const [guardrails, setGuardrails] = useState('');
  const [sections, setSections] = useState<FormSection[]>(() => defaultOnboardingSections());
  const [showFieldSettings, setShowFieldSettings] = useState(false);
  const [selectedField, setSelectedField] = useState<{ sectionId: string; fieldId: string } | null>(null);

  const [freeEnabled, setFreeEnabled] = useState(true);
  const [dayEnabled, setDayEnabled] = useState(true);
  const [dayCredits, setDayCredits] = useState(10);
  const [monthEnabled, setMonthEnabled] = useState(true);
  const [monthCredits, setMonthCredits] = useState(100);
  const [yearEnabled, setYearEnabled] = useState(true);
  const [yearCredits, setYearCredits] = useState(1000);

  const [openaiModel, setOpenaiModel] = useState('openrouter/auto');
  const [knowledgeBaseEnabled, setKnowledgeBaseEnabled] = useState(false);
  const [knowledgeBaseText, setKnowledgeBaseText] = useState('');
  const [fileUploadsEnabled, setFileUploadsEnabled] = useState(false);
  const [toolSerper, setToolSerper] = useState(false);
  const [toolTavily, setToolTavily] = useState(false);
  const [centralToolOptions, setCentralToolOptions] = useState<CentralFalTool[]>([]);
  const [selectedCentralToolIds, setSelectedCentralToolIds] = useState<string[]>([]);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [ttsProvider, setTtsProvider] = useState<AgentTtsProvider>('openai');
  const [ttsVoice, setTtsVoice] = useState('alloy');
  const [ttsSpeed, setTtsSpeed] = useState(1);
  const [ttsInstructions, setTtsInstructions] = useState('');
  const [elevenlabsVoiceId, setElevenlabsVoiceId] = useState('');
  const [elevenlabsLanguageCode, setElevenlabsLanguageCode] = useState('');
  const [sttLocale, setSttLocale] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [onboardingFormEnabled, setOnboardingFormEnabled] = useState(true);
  const [allCategories, setAllCategories] = useState<AgentCategory[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [templateTags, setTemplateTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categorySaving, setCategorySaving] = useState(false);
  const [kbIndexMessage, setKbIndexMessage] = useState<string | null>(null);
  const [kbDraftLoading, setKbDraftLoading] = useState(false);
  const [kbDraftError, setKbDraftError] = useState<string | null>(null);
  const [expandInsLoading, setExpandInsLoading] = useState(false);
  const [expandGrLoading, setExpandGrLoading] = useState(false);
  const [expandSystemError, setExpandSystemError] = useState<string | null>(null);
  const saveFeedbackRef = useRef<HTMLDivElement | null>(null);
  const coverFileInputRef = useRef<HTMLInputElement | null>(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverGenerating, setCoverGenerating] = useState(false);
  const [coverAiPrompt, setCoverAiPrompt] = useState('');
  const [coverLandscapeHint, setCoverLandscapeHint] = useState<string | null>(null);
  const [coverUploadError, setCoverUploadError] = useState<string | null>(null);
  const [avatarRoleKeyword, setAvatarRoleKeyword] = useState('');
  const [avatarGenerating, setAvatarGenerating] = useState(false);
  const [generatedAvatarUrl, setGeneratedAvatarUrl] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [agentFormTab, setAgentFormTab] = useState<AgentFormTab>('basic');
  const [kbDocuments, setKbDocuments] = useState<AgentKbDocument[]>([]);
  const [kbDocUploading, setKbDocUploading] = useState(false);
  const [kbDocError, setKbDocError] = useState<string | null>(null);
  const kbFileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (saveError) {
      saveFeedbackRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [saveError]);

  const applyPricing = useCallback((pricing: AgentPricing | null | undefined) => {
    const h = hydratePricingState(pricing);
    setFreeEnabled(h.freeEnabled);
    setDayEnabled(h.dayEnabled);
    setDayCredits(h.dayCredits);
    setMonthEnabled(h.monthEnabled);
    setMonthCredits(h.monthCredits);
    setYearEnabled(h.yearEnabled);
    setYearCredits(h.yearCredits);
  }, []);

  useEffect(() => {
    const staff = parseAdminStaffSession();
    if (!staff) {
      navigate('/admin');
    }
  }, [navigate]);

  useEffect(() => {
    void (async () => {
      const { data } = await supabase
        .from('central_fal_tools')
        .select('id, display_name, function_name, fal_endpoint')
        .eq('is_active', true)
        .order('display_name');
      setCentralToolOptions((data as CentralFalTool[]) || []);
    })();
  }, []);

  useEffect(() => {
    void (async () => {
      const { data, error } = await supabase
        .from('agent_categories')
        .select('id, name, slug, sort_order')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (error) {
        console.error(error);
        return;
      }
      setAllCategories((data as AgentCategory[]) || []);
    })();
  }, []);

  useEffect(() => {
    if (!isEdit || !templateId) return;
    void (async () => {
      const { data } = await supabase
        .from('agent_template_fal_tools')
        .select('central_fal_tool_id, sort_order')
        .eq('agent_template_id', templateId)
        .order('sort_order', { ascending: true });
      setSelectedCentralToolIds((data || []).map((r: { central_fal_tool_id: string }) => r.central_fal_tool_id));
    })();
  }, [isEdit, templateId]);

  useEffect(() => {
    if (!isEdit || !templateId) return;
    (async () => {
      const { data, error } = await supabase.from('agent_templates').select('*').eq('id', templateId).single();
      if (error || !data) {
        alert('Agent not found');
        navigate('/admin/agents');
        return;
      }
      const t = data as AgentTemplate;
      setName(t.name);
      setPublicDescription((t.public_description ?? '').trim());
      setCoverMediaUrl((t.cover_media_url ?? '').trim());
      setCoverMediaType(t.cover_media_type === 'video' ? 'video' : 'image');
      setSystemInstructions(t.system_instructions);
      setGuardrails((t.guardrails ?? '').trim());
      const ofe = t.onboarding_form_enabled !== false;
      setOnboardingFormEnabled(ofe);
      setSections(normalizeOnboardingSections(t.onboarding_form_schema, { onboardingFormEnabled: ofe }));
      setTemplateTags(Array.isArray(t.tags) && t.tags.length ? [...t.tags] : []);
      const { data: catLinks, error: catLinkErr } = await supabase
        .from('agent_template_categories')
        .select('category_id')
        .eq('agent_template_id', templateId);
      if (catLinkErr) {
        console.error(catLinkErr);
        setSelectedCategoryIds([]);
      } else {
        setSelectedCategoryIds((catLinks || []).map((r: { category_id: string }) => r.category_id));
      }
      applyPricing(t.pricing as AgentPricing);
      setOpenaiModel(t.openai_model || 'openrouter/auto');
      setKnowledgeBaseEnabled(t.knowledge_base_enabled);
      setKnowledgeBaseText((t as { knowledge_base_text?: string }).knowledge_base_text || '');
      setFileUploadsEnabled(t.file_uploads_enabled);
      setToolSerper(t.tool_serper);
      setToolTavily(t.tool_tavily);
      setVoiceEnabled((t as { voice_enabled?: boolean }).voice_enabled ?? false);
      const vc = parseVoiceConfigFromRow((t as AgentTemplate).voice_config);
      setTtsProvider(vc.ttsProvider);
      setTtsVoice(vc.ttsVoice);
      setTtsSpeed(vc.ttsSpeed);
      setTtsInstructions(vc.ttsInstructions);
      setElevenlabsVoiceId(vc.elevenlabsVoiceId);
      setElevenlabsLanguageCode(vc.elevenlabsLanguageCode);
      setSttLocale(vc.sttLocale);
      setIsActive(t.is_active);
      setGeneratedAvatarUrl(null);
      setAvatarRoleKeyword('');
      setAvatarError(null);
      setLoading(false);
    })();
  }, [isEdit, templateId, navigate, applyPricing]);

  // Open Page Settings modal when landing from first save (?openPageSettings=1), then strip query
  useEffect(() => {
    if (!templateId) return;
    if (searchParams.get('openPageSettings') === '1') {
      setShowPageSettings(true);
      navigate(`/admin/agents/edit/${templateId}`, { replace: true });
    }
  }, [templateId, searchParams, navigate]);

  const loadKbDocuments = useCallback(async () => {
    if (!savedAgentId) {
      setKbDocuments([]);
      return;
    }
    const { data, error } = await supabase
      .from('agent_kb_documents')
      .select('*')
      .eq('agent_template_id', savedAgentId)
      .order('created_at', { ascending: false });
    if (error) {
      console.error(error);
      return;
    }
    setKbDocuments((data as AgentKbDocument[]) || []);
  }, [savedAgentId]);

  useEffect(() => {
    void loadKbDocuments();
  }, [loadKbDocuments]);

  const handleOpenFieldSettings = (sectionId: string, fieldId: string) => {
    setSelectedField({ sectionId, fieldId });
    setShowFieldSettings(true);
  };

  const handleCloseFieldSettings = () => {
    setShowFieldSettings(false);
    setSelectedField(null);
  };

  const handleFieldUpdate = (sectionId: string, fieldId: string, updates: Partial<FormField>) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map((field) =>
                field.id === fieldId ? { ...field, ...updates } : field
              ),
            }
          : section
      )
    );
  };

  const getSelectedField = () => {
    if (!selectedField) return null;
    const section = sections.find((s) => s.id === selectedField.sectionId);
    return section?.fields.find((f) => f.id === selectedField.fieldId) ?? null;
  };

  const addSection = () => {
    setSections((prev) => [...prev, createNewSection(prev.length)]);
  };

  const removeSection = (sectionId: string) => {
    setSections((prev) => (prev.length <= 1 ? prev : prev.filter((s) => s.id !== sectionId)));
  };

  const updateSectionTitle = (sectionId: string, title: string) => {
    setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, title } : s)));
  };

  const addField = (sectionId: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? { ...section, fields: [...section.fields, createNewField()] }
          : section
      )
    );
  };

  const removeField = (sectionId: string, fieldId: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? { ...section, fields: section.fields.filter((f) => f.id !== fieldId) }
          : section
      )
    );
  };

  const updateField = (
    sectionId: string,
    fieldId: string,
    updates: Partial<FormSection['fields'][0]>
  ) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map((field) =>
                field.id === fieldId ? { ...field, ...updates } : field
              ),
            }
          : section
      )
    );
  };

  const moveFieldUp = (sectionId: string, fieldIndex: number) => {
    if (fieldIndex === 0) return;
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map((field, index) => {
                if (index === fieldIndex - 1) return section.fields[fieldIndex];
                if (index === fieldIndex) return section.fields[fieldIndex - 1];
                return field;
              }),
            }
          : section
      )
    );
  };

  const moveFieldDown = (sectionId: string, fieldIndex: number) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section || fieldIndex === section.fields.length - 1) return;
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              fields: s.fields.map((field, index) => {
                if (index === fieldIndex) return s.fields[fieldIndex + 1];
                if (index === fieldIndex + 1) return s.fields[fieldIndex];
                return field;
              }),
            }
          : s
      )
    );
  };

  const getAdminAgentContext = () => {
    const viteAppId = (import.meta.env.VITE_APP_ID as string | undefined)?.trim() ?? '';
    const appId = viteAppId || 'web';
    const ok = Boolean(parseAdminStaffSession()?.hy_user_id);
    return { appId, ok };
  };

  const handleCreateKbDraft = async () => {
    setKbDraftError(null);
    const ctx = getAdminAgentContext();
    if (!ctx.ok) {
      setKbDraftError('Sign in with Supabase (admin session) and reload the dashboard.');
      return;
    }
    if (!name.trim() && !publicDescription.trim() && !systemInstructions.trim() && !guardrails.trim()) {
      setKbDraftError('Add at least name, public description, instructions, or guardrails first.');
      return;
    }
    setKbDraftLoading(true);
    try {
      setKnowledgeBaseEnabled(true);
      const { ok, data } = await agentSynthesizeKbDraft({
        appId: ctx.appId,
        agentName: name,
        publicDescription,
        systemInstructions,
        guardrails,
      });
      const err = (data as { error?: string })?.error;
      if (!ok || err) {
        setKbDraftError(err || 'Create KB failed');
        return;
      }
      const draft = String((data as { knowledge_base_text?: string }).knowledge_base_text ?? '').trim();
      if (!draft) {
        setKbDraftError('Model returned empty text.');
        return;
      }
      setKnowledgeBaseText((prev) => {
        const t = prev.trim();
        return t ? `${t}\n\n--- AI-generated ---\n\n${draft}` : draft;
      });
      setKbIndexMessage('Knowledge text generated. Save the agent, then use Rebuild KB index when ready.');
    } finally {
      setKbDraftLoading(false);
    }
  };

  const handleExpandInstructions = async () => {
    setExpandSystemError(null);
    if (!systemInstructions.trim()) {
      setExpandSystemError('Add instructions to expand.');
      return;
    }
    const ctx = getAdminAgentContext();
    if (!ctx.ok) {
      setExpandSystemError('Sign in with Supabase (admin session) and reload the dashboard.');
      return;
    }
    setExpandInsLoading(true);
    try {
      const { ok, data } = await agentExpandAgentSystem({
        appId: ctx.appId,
        expandTarget: 'instructions',
        systemInstructions,
        guardrails,
        fileUploadsEnabled: fileUploadsEnabled,
        voiceEnabled: voiceEnabled,
      });
      const err = (data as { error?: string })?.error;
      if (!ok || err) {
        setExpandSystemError(err || 'Expand failed');
        return;
      }
      const next = String((data as { system_instructions?: string }).system_instructions ?? '').trim();
      if (next) setSystemInstructions(next);
    } finally {
      setExpandInsLoading(false);
    }
  };

  const handleExpandGuardrails = async () => {
    setExpandSystemError(null);
    if (!guardrails.trim()) {
      setExpandSystemError('Add guardrails to expand.');
      return;
    }
    const ctx = getAdminAgentContext();
    if (!ctx.ok) {
      setExpandSystemError('Sign in with Supabase (admin session) and reload the dashboard.');
      return;
    }
    setExpandGrLoading(true);
    try {
      const { ok, data } = await agentExpandAgentSystem({
        appId: ctx.appId,
        expandTarget: 'guardrails',
        systemInstructions,
        guardrails,
        fileUploadsEnabled: fileUploadsEnabled,
        voiceEnabled: voiceEnabled,
      });
      const err = (data as { error?: string })?.error;
      if (!ok || err) {
        setExpandSystemError(err || 'Expand failed');
        return;
      }
      const next = String((data as { guardrails?: string }).guardrails ?? '').trim();
      if (next) setGuardrails(next);
    } finally {
      setExpandGrLoading(false);
    }
  };

  const runKbReindex = async (tid: string | undefined) => {
    if (!tid || !knowledgeBaseEnabled) {
      setKbIndexMessage(null);
      return;
    }
    const { data: pageRow } = await supabase
      .from('pages')
      .select('app_id')
      .eq('agent_template_id', tid)
      .limit(1)
      .maybeSingle();
    const appId = pageRow?.app_id?.trim() || 'web';
    const staff = parseAdminStaffSession();
    if (!staff?.hy_user_id) {
      setKbIndexMessage('KB index skipped: admin session not loaded.');
      return;
    }
    setKbIndexMessage('Reindexing knowledge base…');
    const { ok, data } = await agentReindexKb({
      appId,
      agentTemplateId: tid,
    });
    setKbIndexMessage(
      ok ? `Indexed ${typeof data?.chunks === 'number' ? data.chunks : 0} KB chunks.` : (data?.error as string) || 'Reindex failed'
    );
  };

  const handleCoverFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !savedAgentId) return;
    setCoverUploadError(null);
    setCoverLandscapeHint(null);
    setCoverUploading(true);
    try {
      const hint = await landscapeHintFromFile(file);
      if (hint) setCoverLandscapeHint(hint);
      const { url, type } = await uploadAgentCoverToStorage(file, savedAgentId);
      setCoverMediaUrl(url);
      setCoverMediaType(type);
    } catch (err) {
      setCoverUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setCoverUploading(false);
    }
  };

  const clearCoverMedia = () => {
    setCoverMediaUrl('');
    setCoverMediaType('image');
    setCoverLandscapeHint(null);
    setCoverUploadError(null);
    setCoverAiPrompt('');
  };

  const handleGenerateCoverWithFal = async () => {
    if (!savedAgentId) return;
    setCoverUploadError(null);
    setCoverLandscapeHint(null);
    setCoverGenerating(true);
    try {
      const { ok, publicUrl, errorMessage } = await agentGenerateCoverImage({
        agentTemplateId: savedAgentId,
        prompt: coverAiPrompt.trim() || undefined,
      });
      if (!ok || !publicUrl) {
        throw new Error(errorMessage || 'Cover generation failed');
      }
      setCoverMediaUrl(publicUrl);
      setCoverMediaType('image');
      setCoverLandscapeHint(null);
    } catch (err) {
      setCoverUploadError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setCoverGenerating(false);
    }
  };

  const handleGenerateRoleAvatar = async () => {
    if (!savedAgentId) return;
    const role = avatarRoleKeyword.trim();
    if (!role) {
      setAvatarError('Enter a profession or role (e.g. nutritionist, CBSE teacher).');
      return;
    }
    setAvatarError(null);
    setAvatarGenerating(true);
    try {
      const { ok, publicUrl, errorMessage } = await agentGenerateAvatarImage({
        agentTemplateId: savedAgentId,
        roleKeyword: role,
      });
      if (!ok || !publicUrl) {
        throw new Error(errorMessage || 'Avatar generation failed');
      }
      setGeneratedAvatarUrl(publicUrl);
    } catch (err) {
      setGeneratedAvatarUrl(null);
      setAvatarError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setAvatarGenerating(false);
    }
  };

  const applyGeneratedAvatarToCover = () => {
    if (!generatedAvatarUrl?.trim()) return;
    setCoverMediaUrl(generatedAvatarUrl);
    setCoverMediaType('image');
    setCoverLandscapeHint('Square image: shown letterboxed in the 16:9 cover area; fine for a profile-forward look.');
    setCoverUploadError(null);
  };

  const handleKbFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !savedAgentId) return;
    setKbDocError(null);
    const maxBytes = 15 * 1024 * 1024;
    if (file.size > maxBytes) {
      setKbDocError('File too large (max 15 MB).');
      return;
    }
    setKbDocUploading(true);
    try {
      const rawExt = file.name.includes('.') ? file.name.split('.').pop() : '';
      const ext =
        rawExt && /^[a-zA-Z0-9]+$/.test(rawExt) ? rawExt.toLowerCase() : 'bin';
      const safe = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const path = `agent-templates/${savedAgentId}/kb/${safe}.${ext}`;
      const { error: upErr } = await supabase.storage.from('uploaded').upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type || undefined,
      });
      if (upErr) throw upErr;
      const { data: row, error: insErr } = await supabase
        .from('agent_kb_documents')
        .insert({
          agent_template_id: savedAgentId,
          storage_path: path,
          filename: file.name,
          mime: file.type || null,
          bytes: file.size,
        })
        .select()
        .single();
      if (insErr) {
        await supabase.storage.from('uploaded').remove([path]);
        throw insErr;
      }
      setKbDocuments((prev) => [row as AgentKbDocument, ...prev]);
    } catch (err) {
      setKbDocError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setKbDocUploading(false);
    }
  };

  const deleteKbDocument = async (doc: AgentKbDocument) => {
    setKbDocError(null);
    try {
      await supabase.storage.from('uploaded').remove([doc.storage_path]);
      const { error } = await supabase.from('agent_kb_documents').delete().eq('id', doc.id);
      if (error) throw error;
      setKbDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (err) {
      setKbDocError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const toggleTemplateCategory = (id: string) => {
    setSelectedCategoryIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const removeTemplateTag = (tag: string) => {
    setTemplateTags((prev) => prev.filter((t) => t !== tag));
  };

  const commitTagFromInput = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t) return;
    setTemplateTags((prev) => (prev.includes(t) ? prev : [...prev, t]));
    setTagInput('');
  };

  const addGlobalCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    setCategorySaving(true);
    try {
      let slug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      if (!slug) slug = 'category';
      for (let attempt = 0; attempt < 8; attempt++) {
        const trySlug = attempt === 0 ? slug : `${slug}-${Math.random().toString(36).slice(2, 8)}`;
        const { data, error } = await supabase
          .from('agent_categories')
          .insert({ name, slug: trySlug, sort_order: allCategories.length + attempt })
          .select('id, name, slug, sort_order')
          .single();
        if (!error && data) {
          const row = data as AgentCategory;
          setAllCategories((prev) => [...prev, row].sort((a, b) => a.name.localeCompare(b.name)));
          setSelectedCategoryIds((prev) => (prev.includes(row.id) ? prev : [...prev, row.id]));
          setNewCategoryName('');
          return;
        }
        if (error && !/duplicate|unique|23505/i.test(String(error.message))) {
          window.alert(error.message);
          return;
        }
      }
      window.alert('Could not create category. Try a different name.');
    } finally {
      setCategorySaving(false);
    }
  };

  const save = async () => {
    setSaveError(null);
    setSaveSuccess(null);
    if (!name.trim()) {
      setSaveError('Name is required');
      return;
    }

    if (onboardingFormEnabled) {
      const formValidation = validateFormConfig(sections);
      if (!formValidation.isValid) {
        setSaveError(formValidation.errors[0] || 'Fix onboarding form errors');
        return;
      }

      const allFields = flattenOnboardingFields(sections);
      const seenKeys = new Set<string>();
      for (const field of allFields) {
        const key = getOnboardingAnswerKey(field);
        if (seenKeys.has(key)) {
          setSaveError(`Duplicate field key "${key}" — use unique variable names or field IDs.`);
          return;
        }
        seenKeys.add(key);
      }
    }

    const onboarding_form_schema = onboardingFormEnabled
      ? { sections }
      : { sections: [] as FormSection[] };

    const pricing = buildPricingFromState({
      freeEnabled,
      dayEnabled,
      dayCredits,
      monthEnabled,
      monthCredits,
      yearEnabled,
      yearCredits,
    });

    const hasTier =
      pricing.free === true ||
      typeof pricing.day === 'number' ||
      typeof pricing.month === 'number' ||
      typeof pricing.year === 'number';
    if (!hasTier) {
      setSaveError('Enable at least one pricing option (free or a paid period)');
      return;
    }

    if (voiceEnabled && ttsProvider === 'elevenlabs' && !isValidElevenlabsVoiceIdInput(elevenlabsVoiceId)) {
      setSaveError('ElevenLabs requires a valid Voice ID (copy from the ElevenLabs voice library or dashboard).');
      return;
    }

    const staff = parseAdminStaffSession();

    setSaving(true);
    const coverUrl = coverMediaUrl.trim();
    const voice_config = buildVoiceConfigForSave({
      voiceEnabled,
      ttsProvider,
      ttsVoice,
      ttsSpeed,
      ttsInstructions,
      elevenlabsVoiceId,
      elevenlabsLanguageCode,
      sttLocale,
    });
    const row = {
      name: name.trim(),
      public_description: publicDescription.trim() || null,
      cover_media_url: coverUrl || null,
      cover_media_type: coverUrl ? coverMediaType : null,
      system_instructions: systemInstructions,
      guardrails: guardrails.trim(),
      onboarding_form_enabled: onboardingFormEnabled,
      onboarding_form_schema,
      tags: normalizeTemplateTags(templateTags),
      pricing,
      knowledge_base_enabled: knowledgeBaseEnabled,
      knowledge_base_text: knowledgeBaseText.trim() || null,
      file_uploads_enabled: fileUploadsEnabled,
      tool_serper: toolSerper,
      tool_tavily: toolTavily,
      tool_fal: false,
      voice_enabled: voiceEnabled,
      voice_config,
      openai_model: openaiModel,
      is_active: isActive,
      hy_user_id: staff?.hy_user_id || null,
      updated_at: new Date().toISOString(),
    };

    const persistJunction = async (tid: string) => {
      const { error: delErr } = await supabase.from('agent_template_fal_tools').delete().eq('agent_template_id', tid);
      if (delErr) throw new Error(delErr.message);
      if (selectedCentralToolIds.length === 0) return;
      const rows = selectedCentralToolIds.map((central_fal_tool_id, sort_order) => ({
        agent_template_id: tid,
        central_fal_tool_id,
        sort_order,
      }));
      const { error: insErr } = await supabase.from('agent_template_fal_tools').insert(rows);
      if (insErr) throw new Error(insErr.message);
    };

    const persistTemplateCategories = async (tid: string) => {
      const { error: delErr } = await supabase
        .from('agent_template_categories')
        .delete()
        .eq('agent_template_id', tid);
      if (delErr) throw new Error(delErr.message);
      if (selectedCategoryIds.length === 0) return;
      const { error: insErr } = await supabase.from('agent_template_categories').insert(
        selectedCategoryIds.map((category_id) => ({ agent_template_id: tid, category_id }))
      );
      if (insErr) throw new Error(insErr.message);
    };

    try {
      if (isEdit && templateId) {
        const { data: updated, error } = await supabase
          .from('agent_templates')
          .update(row)
          .eq('id', templateId)
          .select('id');
        if (error) {
          setSaveError(error.message);
        } else if (!updated?.length) {
          setSaveError('Could not save — no matching agent row (check the URL id).');
        } else {
          await persistJunction(templateId);
          await persistTemplateCategories(templateId);
          if (!onboardingFormEnabled) {
            setSections([]);
          }
          setSaveSuccess('Agent saved. You can open Public page settings to set App ID and /p/… URL.');
          await runKbReindex(templateId);
        }
      } else {
        const { data: created, error } = await supabase.from('agent_templates').insert(row).select('id').single();
        if (error) {
          setSaveError(error.message);
        } else if (created?.id) {
          await persistJunction(created.id);
          await persistTemplateCategories(created.id);
          if (!onboardingFormEnabled) {
            setSections([]);
          }
          await runKbReindex(created.id);
          navigate(`/admin/agents/edit/${created.id}?openPageSettings=1`, { replace: true });
        }
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] max-h-[100dvh] min-h-0 flex-col overflow-hidden bg-base">
      <AdminPageToolbar
        title={isEdit ? 'Edit agent' : 'Create agent'}
        subtitle="Configure onboarding, pricing, and behavior — same style as model creation"
      />

      <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain [-webkit-overflow-scrolling:touch]">
        <div className="max-w-6xl mx-auto px-4 py-4 pb-32">
          <div className="space-y-6">
          {saveError && (
            <div
              ref={saveFeedbackRef}
              className="rounded-lg border border-status-error/40 bg-status-error/10 px-4 py-3 text-sm text-status-error"
            >
              {saveError}
            </div>
          )}
          {saveSuccess && (
            <div className="rounded-lg border border-status-success/40 bg-status-success/10 px-4 py-3 text-sm text-status-success">
              {saveSuccess}
            </div>
          )}

          <div className="sticky top-0 z-20 -mx-4 border-b border-subtle bg-base/95 px-4 backdrop-blur-md sm:mx-0 sm:px-0">
            <nav className="flex gap-1 overflow-x-auto pb-px" aria-label="Agent configuration" role="tablist">
              {AGENT_FORM_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={agentFormTab === tab.id}
                  id={`agent-tab-${tab.id}`}
                  aria-controls={`agent-panel-${tab.id}`}
                  onClick={() => setAgentFormTab(tab.id)}
                  className={`shrink-0 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors sm:px-4 ${
                    agentFormTab === tab.id
                      ? 'border-brand text-th-primary'
                      : 'border-transparent text-th-secondary hover:text-th-primary'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Public page — parity with Create Model “Page Settings” (Hyper App ID, /p/ route, etc.) */}
          {agentFormTab === 'public' && (
          <div
            id="agent-panel-public"
            role="tabpanel"
            aria-labelledby="agent-tab-public"
            className="card p-6 space-y-4 border border-brand/20 outline-none"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-th-primary">Public page settings</h2>
                <p className="text-sm text-th-secondary mt-1 max-w-2xl">
                  Set the public URL path under <span className="font-mono text-th-primary">/p/…</span>,{' '}
                  <strong className="text-th-primary">Hyper App ID</strong> (required for session validation), page title,
                  description, and optional example media — same flow as AI model public pages.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!canConfigurePublicPage) return;
                  setShowPageSettings(true);
                }}
                disabled={!canConfigurePublicPage}
                className="flex items-center space-x-2 px-4 py-2 bg-accent text-surface rounded-lg font-medium hover:bg-accent/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              >
                <Globe className="w-4 h-4" />
                <span>Open page settings</span>
              </button>
            </div>
            {!canConfigurePublicPage ? (
              <p className="text-sm text-th-secondary border border-subtle rounded-lg p-3 bg-elevated/50">
                Save this agent once to create a database record. Then you can add App ID and the public route (like{' '}
                <span className="font-mono">/p/my-agent</span>
                ). After your first save you will be taken here automatically to configure the public page.
              </p>
            ) : (
              <p className="text-xs text-th-secondary">
                Tip: use the same App ID as in your Hyper Developer Console for this app. End users open{' '}
                <span className="font-mono">/p/your-path?id=…</span> from the Hyper app.
              </p>
            )}
          </div>
          )}

          {/* Basic info */}
          {agentFormTab === 'basic' && (
          <div
            id="agent-panel-basic"
            role="tabpanel"
            aria-labelledby="agent-tab-basic"
            className="card p-6 space-y-4 outline-none"
          >
            <div>
              <h2 className="text-xl font-bold text-th-primary">Basic information</h2>
              <p className="text-sm text-th-secondary mt-1">
                Internal name, optional public description and cover media — shown on the end-user onboarding form and
                chat.
              </p>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-th-primary">Name *</label>
              <input
                className={INPUT}
                placeholder="e.g. Customer Support Coach"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-th-primary">Public description (optional)</label>
              <textarea
                className={TEXTAREA}
                rows={3}
                placeholder="Short intro under the title — overrides page description when set"
                value={publicDescription}
                onChange={(e) => setPublicDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-th-primary">Categories</label>
              <p className="text-xs text-th-secondary">Used for the agent store. Select one or more; add a new label below if needed.</p>
              {allCategories.length === 0 ? (
                <p className="text-sm text-th-tertiary">No categories yet. Create one with the name field below.</p>
              ) : (
                <ul className="max-h-40 space-y-2 overflow-y-auto rounded-lg border border-subtle/80 bg-elevated/50 p-3">
                  {allCategories.map((c) => (
                    <li key={c.id}>
                      <label className="flex cursor-pointer items-center gap-2 text-sm text-th-primary">
                        <input
                          type="checkbox"
                          className="rounded border-subtle"
                          checked={selectedCategoryIds.includes(c.id)}
                          onChange={() => toggleTemplateCategory(c.id)}
                        />
                        <span>{c.name}</span>
                        <span className="text-xs text-th-tertiary font-mono">{c.slug}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap items-end gap-2 pt-1">
                <div className="min-w-0 flex-1 space-y-1">
                  <label className="text-xs text-th-secondary" htmlFor="new-agent-category">
                    New category name
                  </label>
                  <input
                    id="new-agent-category"
                    className={INPUT}
                    placeholder="e.g. Productivity"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void addGlobalCategory();
                      }
                    }}
                    disabled={categorySaving}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void addGlobalCategory()}
                  disabled={categorySaving || !newCategoryName.trim()}
                  className="button-secondary shrink-0 px-3 py-2 text-sm disabled:opacity-50"
                >
                  {categorySaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add category'}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-th-primary">Tags</label>
              <p className="text-xs text-th-secondary">Free-form labels (normalized to lowercase) for store search. Press Enter to add.</p>
              <div className="flex flex-wrap gap-2">
                {templateTags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 rounded-full border border-subtle/80 bg-elevated px-2.5 py-0.5 text-sm text-th-primary"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => removeTemplateTag(t)}
                      className="rounded p-0.5 text-th-secondary hover:text-th-primary"
                      aria-label={`Remove ${t}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
              <input
                className={INPUT}
                placeholder="Type a tag, Enter to add"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitTagFromInput();
                  }
                }}
              />
            </div>

            <div className="space-y-3 rounded-xl border border-subtle/60 bg-elevated/30 p-4">
              <div>
                <label className="block text-sm font-medium text-th-primary">Profile avatar (AI, optional)</label>
                <p className="text-xs text-th-secondary mt-1">
                  Describe a professional role in a few words. OpenRouter turns it into a prompt; fal runs{' '}
                  <span className="font-mono">openai/gpt-image-2</span> (square). Use as cover to show it in the onboarding
                  hero.
                </p>
              </div>
              <div className="flex flex-wrap items-end gap-2 max-w-2xl">
                <div className="min-w-0 flex-1 space-y-1">
                  <label htmlFor="avatar-role-keyword" className="text-xs text-th-secondary">
                    Role / profession
                  </label>
                  <input
                    id="avatar-role-keyword"
                    className={INPUT}
                    placeholder="e.g. nutritionist, dentist, fitness trainer"
                    value={avatarRoleKeyword}
                    onChange={(e) => {
                      setAvatarRoleKeyword(e.target.value);
                      setAvatarError(null);
                    }}
                    disabled={!canConfigurePublicPage || coverUploading || coverGenerating || avatarGenerating}
                  />
                </div>
                <button
                  type="button"
                  disabled={
                    !canConfigurePublicPage || coverUploading || coverGenerating || avatarGenerating || !avatarRoleKeyword.trim()
                  }
                  onClick={() => void handleGenerateRoleAvatar()}
                  title="OpenRouter → avatar prompt, then fal openai/gpt-image-2 (1:1). Requires OPENROUTER_API_KEY and FAL_KEY on Edge"
                  className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-brand/40 bg-brand/10 px-3 py-2 text-sm font-medium text-brand transition hover:bg-brand/20 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {avatarGenerating ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                  ) : (
                    <User className="h-4 w-4 shrink-0" aria-hidden />
                  )}
                  {avatarGenerating ? 'Generating…' : 'Generate avatar'}
                </button>
              </div>
              {generatedAvatarUrl ? (
                <div className="flex flex-wrap items-start gap-4">
                  <div className="aspect-square w-36 shrink-0 overflow-hidden rounded-xl border border-subtle/80 bg-elevated/60 shadow-inner">
                    <img src={generatedAvatarUrl} alt="Generated avatar preview" className="h-full w-full object-cover" />
                  </div>
                  <div className="min-w-0 flex flex-col gap-2">
                    <button
                      type="button"
                      disabled={coverUploading || coverGenerating || avatarGenerating}
                      onClick={applyGeneratedAvatarToCover}
                      className="inline-flex w-fit items-center gap-2 rounded-lg border border-subtle bg-elevated px-3 py-2 text-sm font-medium text-th-primary transition hover:bg-overlay disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Use as cover image
                    </button>
                    <p className="text-xs text-th-tertiary max-w-md">
                      Your last generated avatar is kept here until you generate again. Set as cover to save with the agent
                      when you click Save.
                    </p>
                  </div>
                </div>
              ) : null}
              {avatarError ? <p className="text-xs text-status-error">{avatarError}</p> : null}
              {!canConfigurePublicPage ? (
                <p className="text-xs text-th-secondary rounded-lg border border-subtle bg-elevated/50 p-2">
                  Save this agent once to generate an avatar to storage.
                </p>
              ) : null}
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-th-primary">Cover media (optional)</label>
                <p className="text-xs text-th-secondary mt-1">
                  Landscape banner (16:9). Shown above the end-user onboarding form. Upload a file, have OpenRouter + fal
                  <span className="font-mono"> openai/gpt-image-2</span> build the hero, or paste a public HTTPS URL.
                </p>
              </div>
              <div className="aspect-video w-full max-w-2xl overflow-hidden rounded-xl border border-subtle/80 bg-elevated/60 shadow-inner">
                {coverMediaUrl.trim() ? (
                  coverMediaType === 'video' ? (
                    <video
                      src={coverMediaUrl.trim()}
                      className="h-full w-full object-cover"
                      controls
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <img
                      src={coverMediaUrl.trim()}
                      alt="Cover preview"
                      className="h-full w-full object-cover"
                    />
                  )
                ) : (
                  <div className="flex h-full min-h-[120px] items-center justify-center px-4 text-center text-sm text-th-tertiary">
                    No cover — upload or paste a URL
                  </div>
                )}
              </div>
              <input
                ref={coverFileInputRef}
                type="file"
                accept="image/*,video/*"
                className="sr-only"
                aria-hidden
                tabIndex={-1}
                onChange={handleCoverFileChange}
              />
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={!canConfigurePublicPage || coverUploading || coverGenerating || avatarGenerating}
                  onClick={() => coverFileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-lg border border-subtle bg-elevated px-3 py-2 text-sm font-medium text-th-primary transition hover:bg-overlay disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {coverUploading ? (
                    <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
                  ) : (
                    <Upload className="h-4 w-4 shrink-0" aria-hidden />
                  )}
                  {coverUploading ? 'Uploading…' : 'Upload image or video'}
                </button>
                <button
                  type="button"
                  disabled={!canConfigurePublicPage || coverUploading || coverGenerating || avatarGenerating}
                  onClick={() => void handleGenerateCoverWithFal()}
                  title="OpenRouter crafts a 16:9 GPT Image 2 prompt from name, description, and optional field; fal renders openai/gpt-image-2. Needs OPENROUTER_API_KEY and FAL_KEY on Edge"
                  className="inline-flex items-center gap-2 rounded-lg border border-brand/40 bg-brand/10 px-3 py-2 text-sm font-medium text-brand transition hover:bg-brand/20 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {coverGenerating ? (
                    <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
                  ) : (
                    <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
                  )}
                  {coverGenerating ? 'Generating…' : 'Generate cover (AI)'}
                </button>
                <button
                  type="button"
                  disabled={!coverMediaUrl.trim() || coverUploading || coverGenerating || avatarGenerating}
                  onClick={clearCoverMedia}
                  className="inline-flex items-center gap-2 rounded-lg border border-subtle/80 px-3 py-2 text-sm text-th-secondary transition hover:border-status-error/50 hover:text-status-error disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Trash2 className="h-4 w-4 shrink-0" aria-hidden />
                  Remove
                </button>
              </div>
              {canConfigurePublicPage ? (
                <div className="space-y-1 max-w-2xl">
                  <label htmlFor="cover-ai-prompt" className="text-xs text-th-secondary">
                    Optional extra direction for the AI cover (OpenRouter still uses name + description; this is merged in)
                  </label>
                  <input
                    id="cover-ai-prompt"
                    className={INPUT}
                    placeholder="e.g. soft blue gradient, minimal, futuristic dashboard hero…"
                    value={coverAiPrompt}
                    onChange={(e) => setCoverAiPrompt(e.target.value)}
                    disabled={coverGenerating || avatarGenerating}
                  />
                </div>
              ) : null}
              {!canConfigurePublicPage ? (
                <p className="text-xs text-th-secondary rounded-lg border border-subtle bg-elevated/50 p-2">
                  Save this agent once to upload a file to storage (same as public page settings).
                </p>
              ) : null}
              {coverLandscapeHint ? (
                <p className="text-xs text-status-warning">{coverLandscapeHint}</p>
              ) : null}
              {coverUploadError ? (
                <p className="text-xs text-status-error">{coverUploadError}</p>
              ) : null}
              <div className="space-y-2 pt-1">
                <label className="block text-sm font-medium text-th-primary">Or paste URL</label>
                <input
                  className={INPUT}
                  placeholder="https://… image or direct video URL"
                  value={coverMediaUrl}
                  onChange={(e) => {
                    setCoverMediaUrl(e.target.value);
                    setCoverLandscapeHint(null);
                    setCoverUploadError(null);
                  }}
                />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs text-th-secondary">Type (when using URL):</span>
                <label className="inline-flex items-center gap-1.5 text-sm text-th-primary cursor-pointer">
                  <input
                    type="radio"
                    name="coverMediaType"
                    checked={coverMediaType === 'image'}
                    onChange={() => setCoverMediaType('image')}
                    className="rounded border-subtle"
                  />
                  Image
                </label>
                <label className="inline-flex items-center gap-1.5 text-sm text-th-primary cursor-pointer">
                  <input
                    type="radio"
                    name="coverMediaType"
                    checked={coverMediaType === 'video'}
                    onChange={() => setCoverMediaType('video')}
                    className="rounded border-subtle"
                  />
                  Video
                </label>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-subtle"
              />
              <span className="text-sm text-th-primary">Active (visible for new public pages)</span>
            </label>
          </div>
          )}

          {/* System instructions */}
          {agentFormTab === 'system' && (
          <div
            id="agent-panel-system"
            role="tabpanel"
            aria-labelledby="agent-tab-system"
            className="card p-6 space-y-4 outline-none"
          >
            <div>
              <h2 className="text-xl font-bold text-th-primary">System instructions</h2>
              <p className="text-sm text-th-secondary mt-1">Base prompt sent to the model (like model description / behavior).</p>
            </div>
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="block text-sm font-medium text-th-primary">Instructions</label>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-lg border border-subtle bg-elevated px-3 py-1.5 text-xs font-medium text-th-primary transition hover:bg-overlay disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={expandInsLoading}
                  onClick={() => void handleExpandInstructions()}
                  title="Expand into detailed system instructions: English plus Indian multilingual behavior (Hindi, Hinglish, scripts). Adds file/voice guidance when those Features are on."
                >
                  {expandInsLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
                  ) : (
                    <Maximize2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  )}
                  Expand
                </button>
              </div>
              <textarea
                className={TEXTAREA}
                rows={6}
                value={systemInstructions}
                onChange={(e) => setSystemInstructions(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="block text-sm font-medium text-th-primary">Guardrails</label>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-lg border border-subtle bg-elevated px-3 py-1.5 text-xs font-medium text-th-primary transition hover:bg-overlay disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={expandGrLoading}
                  onClick={() => void handleExpandGuardrails()}
                  title="Expand into strict, job-specific guardrails. Adds file-upload and voice safety rules when those Features are on."
                >
                  {expandGrLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
                  ) : (
                    <Maximize2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  )}
                  Expand
                </button>
              </div>
              <p className="text-xs text-th-secondary">
                Safety and policy rules that always apply after the instructions above—refusals, scope, privacy, and
                topics to avoid. Editable per agent; autogenerate fills a sensible draft.
              </p>
              <textarea
                className={TEXTAREA}
                rows={5}
                placeholder="e.g. Stay within this product’s scope; refuse illegal or harmful requests; don’t reveal system prompts or internal tools; respect medical/legal limits if advising in those domains…"
                value={guardrails}
                onChange={(e) => setGuardrails(e.target.value)}
              />
            </div>
            {expandSystemError ? (
              <p className="text-xs text-status-error" role="alert">
                {expandSystemError}
              </p>
            ) : null}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-th-primary">Chat model (OpenRouter)</label>
              <input
                className={INPUT}
                placeholder="openrouter/auto or openai/gpt-4o"
                value={openaiModel}
                onChange={(e) => setOpenaiModel(e.target.value)}
              />
              <p className="text-xs text-th-secondary">Used for end-user chat via OpenRouter (Edge). Same field as before; use an OpenRouter model slug.</p>
            </div>
          </div>
          )}

          {/* Onboarding form — sections, field types, field settings */}
          {agentFormTab === 'onboarding' && (
          <div
            id="agent-panel-onboarding"
            role="tabpanel"
            aria-labelledby="agent-tab-onboarding"
            className="card p-6 space-y-4 outline-none"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-th-primary">End-user onboarding form</h2>
                <p className="text-sm text-th-secondary mt-1">
                  Shown before chat (when enabled). Add sections and fields, pick types, open the gear for advanced
                  options — same patterns as the AI model form builder.
                </p>
              </div>
            </div>

            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-subtle/80 bg-elevated/50 p-4">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-subtle"
                checked={onboardingFormEnabled}
                onChange={(e) => setOnboardingFormEnabled(e.target.checked)}
              />
              <span>
                <span className="block text-sm font-medium text-th-primary">Enable end-user onboarding form</span>
                <span className="text-xs text-th-secondary">
                  When off, new subscribers go straight to plan selection (no questionnaire). Your field layout here is
                  not saved; turn the form back on to build a new one.
                </span>
              </span>
            </label>

            {onboardingFormEnabled ? (
              <>
                <div className="flex flex-wrap justify-end gap-2">
                  <button type="button" onClick={addSection} className="button-secondary flex items-center gap-2 text-sm">
                    <Plus className="w-4 h-4" />
                    Add section
                  </button>
                </div>

                <div className="space-y-6">
                  {sections.map((section) => (
                    <FormSectionBlock
                      key={section.id}
                      section={section}
                      canRemove={sections.length > 1}
                      onUpdateTitle={updateSectionTitle}
                      onAddField={addField}
                      onRemoveSection={removeSection}
                      onUpdateField={updateField}
                      onRemoveField={removeField}
                      onOpenFieldSettings={handleOpenFieldSettings}
                      onMoveFieldUp={moveFieldUp}
                      onMoveFieldDown={moveFieldDown}
                    />
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-th-secondary rounded-lg border border-subtle/60 bg-elevated/40 p-4">
                Onboarding form is off for this agent. End users will only see plan options before the chat starts.
              </p>
            )}
          </div>
          )}

          {/* Pricing */}
          {agentFormTab === 'pricing' && (
          <div
            id="agent-panel-pricing"
            role="tabpanel"
            aria-labelledby="agent-tab-pricing"
            className="card p-6 space-y-4 outline-none"
          >
            <div>
              <h2 className="text-xl font-bold text-th-primary">Pricing & credits</h2>
              <p className="text-sm text-th-secondary mt-1">
                Enable billing periods and set Hyper credits charged per period. Users pick one option at onboarding.
              </p>
            </div>

            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-subtle hover:bg-overlay/50 transition-colors">
                <input
                  type="checkbox"
                  checked={freeEnabled}
                  onChange={(e) => setFreeEnabled(e.target.checked)}
                  className="rounded border-subtle"
                />
                <div>
                  <span className="text-sm font-medium text-th-primary">Free tier</span>
                  <p className="text-xs text-th-secondary">No credits deducted; user can subscribe for $0.</p>
                </div>
              </label>

              <div className="border border-subtle rounded-lg p-4 space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dayEnabled}
                    onChange={(e) => setDayEnabled(e.target.checked)}
                    className="rounded border-subtle"
                  />
                  <span className="text-sm font-medium text-th-primary">One Day Pass</span>
                </label>
                {dayEnabled && (
                  <div className="pl-8 sm:flex sm:items-center gap-3">
                    <label className="text-xs text-th-secondary whitespace-nowrap">Credits</label>
                    <input
                      type="number"
                      min={0}
                      className={`${INPUT} max-w-[140px]`}
                      value={dayCredits}
                      onChange={(e) => setDayCredits(Number(e.target.value))}
                    />
                  </div>
                )}
              </div>

              <div className="border border-subtle rounded-lg p-4 space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={monthEnabled}
                    onChange={(e) => setMonthEnabled(e.target.checked)}
                    className="rounded border-subtle"
                  />
                  <span className="text-sm font-medium text-th-primary">Monthly Pass</span>
                </label>
                {monthEnabled && (
                  <div className="pl-8 sm:flex sm:items-center gap-3">
                    <label className="text-xs text-th-secondary whitespace-nowrap">Credits</label>
                    <input
                      type="number"
                      min={0}
                      className={`${INPUT} max-w-[140px]`}
                      value={monthCredits}
                      onChange={(e) => setMonthCredits(Number(e.target.value))}
                    />
                  </div>
                )}
              </div>

              <div className="border border-subtle rounded-lg p-4 space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={yearEnabled}
                    onChange={(e) => setYearEnabled(e.target.checked)}
                    className="rounded border-subtle"
                  />
                  <span className="text-sm font-medium text-th-primary">Yearly Pass</span>
                </label>
                {yearEnabled && (
                  <div className="pl-8 sm:flex sm:items-center gap-3">
                    <label className="text-xs text-th-secondary whitespace-nowrap">Credits</label>
                    <input
                      type="number"
                      min={0}
                      className={`${INPUT} max-w-[140px]`}
                      value={yearCredits}
                      onChange={(e) => setYearCredits(Number(e.target.value))}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          )}

          {/* Knowledge base */}
          {agentFormTab === 'knowledge' && (
          <div
            id="agent-panel-knowledge"
            role="tabpanel"
            aria-labelledby="agent-tab-knowledge"
            className="card p-6 space-y-4 outline-none"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-th-primary">Knowledge base</h2>
                <p className="text-sm text-th-secondary mt-1">
                  Paste notes and/or upload documents. On <strong className="text-th-primary">Rebuild KB index</strong>, text
                  is extracted, split into chunks, embedded (OpenRouter, 1536-d), and stored in{' '}
                  <span className="font-mono text-th-primary">agent_kb_chunks</span> (pgvector) for retrieval at chat time.
                </p>
              </div>
              {savedAgentId && (
                <button
                  type="button"
                  className="button-secondary text-sm shrink-0"
                  disabled={
                    !knowledgeBaseEnabled ||
                    (!knowledgeBaseText.trim() && kbDocuments.length === 0)
                  }
                  onClick={() => runKbReindex(savedAgentId)}
                >
                  Rebuild KB index
                </button>
              )}
            </div>
            {kbIndexMessage && (
              <p className="text-xs text-th-secondary border border-subtle rounded-lg px-3 py-2 bg-elevated/50">
                {kbIndexMessage}
              </p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex min-w-0 flex-1 items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={knowledgeBaseEnabled}
                  onChange={(e) => setKnowledgeBaseEnabled(e.target.checked)}
                  className="rounded border-subtle shrink-0"
                />
                <span className="text-sm text-th-primary">
                  Enable knowledge base (RAG + fallback full-text)
                </span>
              </label>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-lg border border-subtle bg-elevated px-3 py-2 text-sm font-medium text-th-primary transition hover:bg-overlay disabled:cursor-not-allowed disabled:opacity-50 shrink-0"
                disabled={kbDraftLoading}
                onClick={() => void handleCreateKbDraft()}
                title="Generate detailed KB text: tips, topic knowledge, metrics, and expertise from your agent fields (AI)"
              >
                {kbDraftLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
                ) : (
                  <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
                )}
                Create KB
              </button>
            </div>
            {kbDraftError ? (
              <p className="text-xs text-status-error" role="alert">
                {kbDraftError}
              </p>
            ) : null}
            {knowledgeBaseEnabled && (
              <>
                <textarea
                  className={TEXTAREA}
                  rows={8}
                  placeholder="Paste reference text for this agent (optional if you use files only)..."
                  value={knowledgeBaseText}
                  onChange={(e) => setKnowledgeBaseText(e.target.value)}
                />
                <div className="space-y-3 rounded-xl border border-subtle/80 bg-elevated/40 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-th-primary">Knowledge files</h3>
                      <p className="text-xs text-th-secondary mt-1 max-w-2xl">
                        PDF, Word (.docx), plain text, Markdown, CSV, JSON, HTML — uploaded to storage, then vectorized on
                        rebuild. Same pgvector index as pasted text; chunk metadata includes the file name for traceability.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                      <input
                        ref={kbFileInputRef}
                        type="file"
                        accept=".pdf,.docx,.txt,.md,.csv,.tsv,.json,.html,.htm,text/*,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        className="sr-only"
                        aria-hidden
                        tabIndex={-1}
                        onChange={handleKbFileChange}
                      />
                      <button
                        type="button"
                        disabled={!canConfigurePublicPage || kbDocUploading}
                        onClick={() => kbFileInputRef.current?.click()}
                        className="inline-flex items-center gap-2 rounded-lg border border-subtle bg-elevated px-3 py-2 text-sm font-medium text-th-primary transition hover:bg-overlay disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {kbDocUploading ? (
                          <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
                        ) : (
                          <FileText className="h-4 w-4 shrink-0" aria-hidden />
                        )}
                        {kbDocUploading ? 'Uploading…' : 'Add file'}
                      </button>
                    </div>
                  </div>
                  {!canConfigurePublicPage ? (
                    <p className="text-xs text-th-secondary">Save the agent once to upload knowledge files.</p>
                  ) : null}
                  {kbDocError ? <p className="text-xs text-status-error">{kbDocError}</p> : null}
                  {kbDocuments.length === 0 ? (
                    <p className="text-xs text-th-tertiary">No files yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {kbDocuments.map((doc) => (
                        <li
                          key={doc.id}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-subtle/60 bg-surface/80 px-3 py-2 text-sm"
                        >
                          <span className="flex min-w-0 items-center gap-2 text-th-primary">
                            <FileText className="h-4 w-4 shrink-0 text-th-secondary" aria-hidden />
                            <span className="truncate font-medium" title={doc.filename}>
                              {doc.filename}
                            </span>
                            {typeof doc.bytes === 'number' ? (
                              <span className="text-xs text-th-tertiary tabular-nums">
                                {(doc.bytes / 1024).toFixed(1)} KB
                              </span>
                            ) : null}
                          </span>
                          <button
                            type="button"
                            className="shrink-0 rounded-md border border-subtle/80 px-2 py-1 text-xs text-th-secondary hover:border-status-error/50 hover:text-status-error"
                            onClick={() => void deleteKbDocument(doc)}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
          )}

          {/* Features & tools */}
          {agentFormTab === 'capabilities' && (
          <div
            id="agent-panel-capabilities"
            role="tabpanel"
            aria-labelledby="agent-tab-capabilities"
            className="space-y-6 outline-none"
          >
          <div className="card p-6 space-y-4">
            <h2 className="text-xl font-bold text-th-primary">Features</h2>
            <p className="text-sm text-th-secondary">
              These flags control what end users see in the public agent chat. API keys for tools and voice stay in Supabase Edge secrets, not in the browser.
            </p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={fileUploadsEnabled}
                onChange={(e) => setFileUploadsEnabled(e.target.checked)}
                className="rounded border-subtle"
              />
              <span className="text-sm text-th-primary">
                Allow file uploads in chat (text, images, PDF, Word — matches composer attachments; stored per user via Edge)
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={voiceEnabled}
                onChange={(e) => setVoiceEnabled(e.target.checked)}
                className="rounded border-subtle"
              />
              <span className="text-sm text-th-primary">
                Voice input and spoken replies (Whisper + TTS on `agent-api`). Requires `OPENAI_API_KEY` for transcription; OpenAI TTS or `ELEVENLABS_API_KEY` for ElevenLabs. When off, only the text composer is shown.
              </span>
            </label>

            {voiceEnabled && (
              <div className="ml-6 mt-3 space-y-4 rounded-lg border border-subtle bg-elevated/40 p-4">
                <p className="text-xs text-th-secondary">
                  Speech-to-text language applies to Whisper and the browser speech API. TTS provider and voice are resolved on the server from this agent (end users cannot change them).
                </p>

                <div>
                  <label className="block text-xs font-medium text-th-secondary mb-1">Speech-to-text language</label>
                  <select
                    value={sttLocale}
                    onChange={(e) => setSttLocale(e.target.value)}
                    className={`${INPUT} py-2 text-sm`}
                  >
                    {STT_LOCALE_OPTIONS.map((o) => (
                      <option key={o.value || 'auto'} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <span className="block text-xs font-medium text-th-secondary mb-2">Spoken replies (TTS)</span>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-th-primary">
                      <input
                        type="radio"
                        name="tts-provider"
                        checked={ttsProvider === 'openai'}
                        onChange={() => setTtsProvider('openai')}
                        className="rounded border-subtle"
                      />
                      OpenAI
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-th-primary">
                      <input
                        type="radio"
                        name="tts-provider"
                        checked={ttsProvider === 'elevenlabs'}
                        onChange={() => setTtsProvider('elevenlabs')}
                        className="rounded border-subtle"
                      />
                      ElevenLabs (eleven_v3)
                    </label>
                  </div>
                  {ttsProvider === 'elevenlabs' ? (
                    <p className="text-xs text-th-secondary mt-2">
                      Higher quality and expressiveness; typically slower than OpenAI streaming. Set{' '}
                      <code className="text-xs">ELEVENLABS_API_KEY</code> on the `agent-api` function. See{' '}
                      <a
                        href="https://elevenlabs.io/docs/overview/models"
                        target="_blank"
                        rel="noreferrer"
                        className="text-brand hover:underline"
                      >
                        ElevenLabs models
                      </a>
                      .
                    </p>
                  ) : null}
                </div>

                {ttsProvider === 'openai' ? (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-th-secondary mb-1">Voice</label>
                      <select
                        value={ttsVoice}
                        onChange={(e) => setTtsVoice(e.target.value)}
                        className={`${INPUT} py-2 text-sm`}
                      >
                        {OPENAI_TTS_VOICE_OPTIONS.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-th-secondary mb-1">
                        Speed ({ttsSpeed.toFixed(2)}×)
                      </label>
                      <input
                        type="range"
                        min={0.25}
                        max={4}
                        step={0.05}
                        value={ttsSpeed}
                        onChange={(e) => setTtsSpeed(Number(e.target.value))}
                        className="w-full max-w-md"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-th-secondary mb-1">
                        Speaking style (optional)
                      </label>
                      <textarea
                        value={ttsInstructions}
                        onChange={(e) => setTtsInstructions(e.target.value)}
                        placeholder="e.g. Warm Indian English; short clear sentences for voice; natural Hindi pronunciation when the text is in Hindi."
                        className={`${TEXTAREA} min-h-[88px] text-sm`}
                        maxLength={4000}
                      />
                      <p className="text-xs text-th-secondary mt-1">
                        Appended to automatic pronunciation hints for Indic scripts (OpenAI{' '}
                        <code className="text-xs">gpt-4o-mini-tts</code>).
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-th-secondary mb-1">ElevenLabs voice ID</label>
                      <input
                        type="text"
                        value={elevenlabsVoiceId}
                        onChange={(e) => setElevenlabsVoiceId(e.target.value)}
                        placeholder="e.g. JBFqnCBsd6RMkjVDRZzb"
                        className={`${INPUT} py-2 text-sm font-mono`}
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-th-secondary mb-1">
                        Language code (optional, ISO 639-1 / ElevenLabs)
                      </label>
                      <input
                        type="text"
                        value={elevenlabsLanguageCode}
                        onChange={(e) => setElevenlabsLanguageCode(e.target.value.trim().toLowerCase())}
                        placeholder="e.g. eng, hin — leave empty to infer from text"
                        className={`${INPUT} py-2 text-sm font-mono`}
                        autoComplete="off"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="card p-6 space-y-3">
            <div>
              <h2 className="text-xl font-bold text-th-primary">Tools during chat</h2>
              <p className="text-sm text-th-secondary mt-1">
                When enabled, the model can call tools during a reply (web search and/or central FAL tools). Configure secrets on the `agent-api` function: Serper →{' '}
                <code className="text-xs text-th-primary/90">SERPER_API_KEY</code>, Tavily →{' '}
                <code className="text-xs text-th-primary/90">TAVILY_API_KEY</code>, FAL →{' '}
                <code className="text-xs text-th-primary/90">FAL_KEY</code>. If a toggle is on but the key is missing, that tool is skipped for the request.
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={toolSerper}
                onChange={(e) => setToolSerper(e.target.checked)}
                className="rounded border-subtle"
              />
              <span className="text-sm text-th-primary">Serper — Google web search (titles, links, snippets)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={toolTavily}
                onChange={(e) => setToolTavily(e.target.checked)}
                className="rounded border-subtle"
              />
              <span className="text-sm text-th-primary">Tavily — web search with optional answer summary</span>
            </label>
            <div className="rounded-lg border border-subtle bg-elevated/50 p-3 space-y-2">
              <p className="text-sm font-medium text-th-primary">Central FAL tools</p>
              <p className="text-xs text-th-secondary">
                Choose one or more tools from the <code className="text-xs">central_fal_tools</code> registry (managed
                in the database). Requires{' '}
                <code className="text-xs">FAL_KEY</code> on <code className="text-xs">agent-api</code>. Outputs are saved to the user&apos;s asset library when possible.
              </p>
              {centralToolOptions.length === 0 ? (
                <p className="text-xs text-th-secondary">No active central tools in the registry yet.</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {centralToolOptions.map((t) => (
                    <label key={t.id} className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded border-subtle mt-0.5"
                        checked={selectedCentralToolIds.includes(t.id)}
                        onChange={() => {
                          setSelectedCentralToolIds((prev) =>
                            prev.includes(t.id) ? prev.filter((x) => x !== t.id) : [...prev, t.id]
                          );
                        }}
                      />
                      <span className="text-sm text-th-primary">
                        <span className="font-medium">{t.display_name}</span>{' '}
                        <code className="text-xs text-brand">{t.function_name}</code>
                        <span className="block text-xs text-th-secondary">{t.fal_endpoint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          </div>
          )}

          </div>
        </div>
      </main>

      {/* Above modals (z-modal-content = 50) so Save still works when Page Settings / previews are open */}
      <div className="fixed bottom-0 left-0 right-0 z-[60] border-t border-subtle bg-surface/95 backdrop-blur-md px-4 py-3 shadow-[0_-8px_24px_rgba(0,0,0,0.12)]">
        <div className="max-w-6xl mx-auto flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 text-sm">
            {saveError && (
              <p className="text-status-error truncate" title={saveError}>
                {saveError}
              </p>
            )}
            {saveSuccess && !saveError && (
              <p className="text-status-success truncate" title={saveSuccess}>
                {saveSuccess}
              </p>
            )}
            {!saveError && !saveSuccess && (
              <p className="text-th-secondary hidden sm:block">Changes are saved to Supabase when you click Save.</p>
            )}
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <button
              type="button"
              className="button-primary flex items-center gap-2"
              disabled={saving || !name.trim()}
              onClick={() => void save()}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save agent'}
            </button>
            <button type="button" className="button-secondary" onClick={() => navigate('/admin/agents')}>
              Cancel
            </button>
          </div>
        </div>
      </div>

      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      {canConfigurePublicPage && savedAgentId && (
        <PageSettingsModal
          isOpen={showPageSettings}
          onClose={() => setShowPageSettings(false)}
          modelconfigId=""
          modelName={name.trim() || 'Agent'}
          variant="agent"
          agentTemplateId={savedAgentId}
        />
      )}

      <FieldSettingsModal
        isOpen={showFieldSettings}
        onClose={handleCloseFieldSettings}
        field={getSelectedField()}
        sectionId={selectedField?.sectionId || null}
        onUpdate={handleFieldUpdate}
      />
    </div>
  );
};
